import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import ActionIcon from '../../../src/assets/icons/ActionIcon.svg';
import apiService from '../../services/api';
import { DATE_FORMATS } from '../../constants';
import { usePagination } from '../../hooks/usePagination';
import Pagination from '../../components/ui/Pagination';
import SortableTable, { SortableColumn } from '../../components/common/SortableTable';
import {
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  Box,
  Chip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import CustomLoading from '../../components/common/CustomLoading';
import useCodeCommonByCategory from '../../hooks/useCodeCommonByCategory';
import { formatMobileNumber } from '../../utils/formatters';
import { useAppSelector } from '../../hooks/useAuth';
import { getHubId } from '../../utils/cookieUtils';
import useRoleBasedFiltering from '../../hooks/useRoleBasedFiltering';

dayjs.extend(utc);

function parseRideManagementResponse(response: unknown): {
  items: unknown[];
  totalCount: number;
  overDue: number | null;
  onTime: number | null;
  absconded: number | null;
} {
  const raw = response as { data?: Record<string, unknown> } | null | undefined;
  const data = raw?.data;
  if (data == null || typeof data !== 'object') {
    return { items: [], totalCount: 0, overDue: null, onTime: null, absconded: null };
  }
  const itemsRaw = data.items ?? (data as { Items?: unknown[] }).Items;
  const items = Array.isArray(itemsRaw) ? itemsRaw : [];
  const totalCandidate = data.totalCount ?? (data as { TotalCount?: unknown }).TotalCount ?? 0;
  const totalCount =
    typeof totalCandidate === 'number' && Number.isFinite(totalCandidate)
      ? totalCandidate
      : Number.parseInt(String(totalCandidate), 10) || 0;
  const od = data.over_duecount;
  const ot = data.ontime_count;
  const ab = data.absconded_count;
  return {
    items,
    totalCount,
    overDue: typeof od === 'number' ? od : null,
    onTime: typeof ot === 'number' ? ot : null,
    absconded: typeof ab === 'number' ? ab : null,
  };
}

interface RideData {
  id: string;
  bookingId: string;
  dueDate: string;
  userName: string;
  mobileNo: string;
  vehicleName: string;
  vehicleNumber?: string;
  registrationNumber?: string;
  traveled: string;
  status: string;
  isOverdue: boolean;
  hubid?: string | number;
  hubmasterid?: string | number;
  userstatus?: number | null;
}

interface ApiRideResponse {
  name: string;
  phonenumber: string;
  vehiclename?: string | null;
  bookingmasterid: number;
  bookinguid?: string | null;
  travelkm: number;
  duedate: string;
  plandue: number | null;
  vehiclenum?: string | null;
  registrationnumber?: string | null;
  userstatus?: number | null;
  hubid?: string | number;
  hubmasterid?: string | number;
}

const StatusChip = ({ status }: { status: string }) => {
  if (!status) return null;

  const STATUS_STYLES: Record<string, { bg: string; color: string }> = {
    Absconded: { bg: "rgba(255, 235, 205, 1)", color: "#FF3B30" },
    Overdue: { bg: "rgba(255, 227, 227, 1)", color: "rgba(197, 0, 0, 1)" },
    "On Time": { bg: "rgba(212, 255, 199, 1)", color: "rgba(31, 113, 13, 1)" },
  };

  return (
    <Chip
      label={status}
      size="small"
      sx={{
        backgroundColor: STATUS_STYLES[status]?.bg || "rgba(245, 245, 245, 1)",
        color: STATUS_STYLES[status]?.color || "rgba(117, 117, 117, 1)",
        fontWeight: 600,
        fontSize: '0.7rem',
        height: 20,
      }}
    />
  );
};


const RideManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [filteredRideData, setFilteredRideData] = useState<RideData[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [summaryCounts, setSummaryCounts] = useState<{
    overDue: number | null;
    onTime: number | null;
    absconded: number | null;
  }>({ overDue: null, onTime: null, absconded: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const initialFilters = location.state?.filters || {};
  const initialPage = location.state?.page || 0;
  const [searchTerm, setSearchTerm] = useState(initialFilters.searchTerm || '');
  const [dueDate, setDueDate] = useState<Dayjs | null>(
    initialFilters.dueDate ? dayjs(initialFilters.dueDate) : null
  );
  const [retryCount, setRetryCount] = useState(0);
  const isInitialMount = useRef(true);
  const searchRef = useRef(searchTerm);
  const dueRef = useRef(dueDate);
  searchRef.current = searchTerm;
  dueRef.current = dueDate;

  const { getDisplayNameByCodeValue } = useCodeCommonByCategory('UserStatus');

  const { user } = useAppSelector((state) => state.auth);
  const isSuperAdmin = user?.roleid === 1;
  const { filterByRole } = useRoleBasedFiltering<RideData>();
  const resolveHubMasterIdForRides = useCallback((): number => {
    if (isSuperAdmin) return 0;
    const hid = getHubId();
    return hid != null && Number.isFinite(Number(hid)) ? Number(hid) : 0;
  }, [isSuperAdmin]);

  const pagination = usePagination({
    totalItems: totalCount,
    initialPage: initialPage,
    initialRowsPerPage: 10,
  });

  const transformApiResponse = (apiData: ApiRideResponse[]): RideData[] => {
    // Helper function to handle null, undefined, and empty strings
    const getValueOrNA = (value: any): string => {
      if (value === null || value === undefined || value === "") {
        return "N/A";
      }
      return String(value);
    };

    return apiData.map((item) => {
      // Check plandue - it can be 1 (number) or "1" (string) or null
      // Convert to string for comparison to handle both number and string cases
      const plandueValue = item.plandue;
      const isOverdue = plandueValue === 1 || String(plandueValue) === "1";
      const baseStatus = isOverdue ? "Overdue" : "On Time";

      return {
        id: item.bookingmasterid.toString(),
        bookingId: String(item.bookingmasterid),
        dueDate: item.duedate,
        userName: getValueOrNA(item.name),
        mobileNo: formatMobileNumber(item.phonenumber) || "",
        vehicleName: getValueOrNA(item.vehiclename),
        vehicleNumber: getValueOrNA(item.vehiclenum),
        registrationNumber: getValueOrNA(item.registrationnumber),
        traveled: `${item.travelkm ?? 0}KM`,
        status: baseStatus,
        isOverdue: isOverdue,
        hubid: item.hubid ?? item.hubmasterid,
        hubmasterid: item.hubmasterid ?? item.hubid,
        userstatus: item.userstatus ?? null,
      };
    });
  };

  const loadRides = useCallback(
    async (pageOverride?: number) => {
      const pageNum = pageOverride !== undefined ? pageOverride : pagination.page;
      const pageSize = Math.max(1, Number(pagination.rowsPerPage) || 10);
      const bookingid = searchRef.current.trim() || null;
      const d = dueRef.current;
      const duedate = d ? d.endOf('day').utcOffset(0, true).toISOString() : null;

      setLoading(true);
      setError(null);
      try {
        const response = await apiService.getRideManagementData({
          bookingid,
          duedate,
          pageNumber: pageNum + 1,
          pageSize,
          hubmasterid: resolveHubMasterIdForRides(),
        });
        const parsed = parseRideManagementResponse(response);
        setTotalCount(parsed.totalCount);
        setSummaryCounts({
          overDue: parsed.overDue,
          onTime: parsed.onTime,
          absconded: parsed.absconded,
        });
        const mapped = transformApiResponse(parsed.items as ApiRideResponse[]);
        const scopedRows = filterByRole(mapped, "hubmasterid");
        setFilteredRideData(scopedRows);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to load ride data';
        setError(message);
        setFilteredRideData([]);
        setTotalCount(0);
        setSummaryCounts({ overDue: null, onTime: null, absconded: null });
      } finally {
        setLoading(false);
      }
    },
    [pagination.page, pagination.rowsPerPage, resolveHubMasterIdForRides, filterByRole]
  );

  const loadRidesRef = useRef(loadRides);
  loadRidesRef.current = loadRides;

  const resolveStatus = (ride: RideData): string => {
    // First check if userstatus maps to "Absconded" - this takes priority
    const userStatusDisplay = getDisplayNameByCodeValue(ride.userstatus);

    // If userstatus maps to "Absconded", use it
    if (userStatusDisplay && userStatusDisplay.toLowerCase() === "absconded") {
      return "Absconded";
    }

    // Otherwise, prioritize the base status (Overdue/On Time) from plandue
    // This ensures overdue status is shown correctly
    return ride.status;
  };

  const columns: SortableColumn<RideData>[] = [
    {
      id: 'bookingId',
      label: 'Booking Id',
      sortable: true,
    },
    {
      id: 'userName',
      label: 'User Name',
      sortable: true,
    },
    {
      id: 'mobileNo',
      label: 'Mobile No.',
      sortable: true,
    },
    {
      id: 'vehicleName',
      label: 'Vehicle Name',
      sortable: true,
    },
    {
      id: 'vehicleNumber',
      label: 'Vehicle Number',
      sortable: true,
    },
    {
      id: 'registrationNumber',
      label: 'Registration Number',
      sortable: true,
    },
    {
      id: 'traveled',
      label: 'Traveled',
      sortable: true,
    },
    {
      id: 'dueDate',
      label: 'Due Date',
      sortable: true,
      render: (row: RideData) => {
        const effectiveStatus = resolveStatus(row);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              sx={{
                color: effectiveStatus === "Absconded" || effectiveStatus === "Overdue" ? '#d32f2f' : 'inherit',
                fontWeight: effectiveStatus === "Absconded" || effectiveStatus === "Overdue" ? 600 : 'normal',
              }}
            >
              {DATE_FORMATS.formatDate(row.dueDate)}
            </Typography>
            {(effectiveStatus === "Absconded" || effectiveStatus === "Overdue") && (
              <StatusChip status={effectiveStatus} />
            )}
          </Box>
        );
      }
    },
    {
      id: 'actions' as keyof RideData,
      label: 'Action',
      align: 'center',
      sortable: false,
      render: (row: RideData) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
          <Button
            variant="text"
            sx={{ p: 0, minWidth: "auto" }}
            onClick={() => handleViewRideDetails(row.id)}
          >
            <img src={ActionIcon} alt="Action" width={30} height={30} />
          </Button>
        </Box>
      )
    },
  ];

  useEffect(() => {
    void loadRides();
  }, [loadRides]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    pagination.resetPage();
    const timer = setTimeout(() => {
      navigate(location.pathname, {
        replace: true,
        state: {
          ...((location.state as object) || {}),
          filters: {
            searchTerm,
            dueDate: dueDate ? dueDate.toISOString() : null,
          },
          page: 0,
        },
      });
      void loadRidesRef.current(0);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, dueDate]);

  useEffect(() => {
    const state = location.state as { refresh?: boolean; refreshHubs?: boolean };
    if (state?.refresh || state?.refreshHubs) {
      void loadRides(0);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname, loadRides]);

  useEffect(() => {
    try {
      const d = dueDate;
      const exportFilters = {
        bookingid: searchTerm.trim() || null,
        duedate: d ? d.endOf('day').utcOffset(0, true).toISOString() : null,
        hubmasterid: resolveHubMasterIdForRides(),
        pageNumber: 1,
        pageSize: 10000,
      };
      localStorage.setItem('rideExportFilters', JSON.stringify(exportFilters));
    } catch {
      // ignore
    }
  }, [searchTerm, dueDate, resolveHubMasterIdForRides]);

  const handleViewRideDetails = (rideId: string) => {
    const filters = {
      searchTerm,
      dueDate: dueDate ? dueDate.toISOString() : null,
    };
    navigate(`/ride-management/ride-details/${rideId}`, {
      state: { filters, page: pagination.page },
    });
  };

  const handleRetry = () => {
    setRetryCount((prev) => prev + 1);
    setError(null);
    void loadRides();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container fluid className="px-0">
        {error && (
          <Alert
            severity="error"
            className="mb-3"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={handleRetry}
                disabled={loading}
              >
                Retry
              </Button>
            }
            onClose={() => setError(null)}
          >
            {error}
            {retryCount > 0 && ` (Retry ${retryCount})`}
          </Alert>
        )}

        {totalCount > 0 && (
          <Box className="mb-3">
            <Paper sx={{ p: 2, backgroundColor: '#fff3e0', border: '1px solid #ffb74d' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6" sx={{ color: '#e65100', fontWeight: 600 }}>
                  Ride Management Summary
                </Typography>
                <Chip
                  label={`${summaryCounts.absconded ??
                    filteredRideData.filter((ride) => resolveStatus(ride) === 'Absconded').length
                    } Absconded`}
                  sx={{
                    backgroundColor: '#FF3B30',
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label={`${summaryCounts.overDue ??
                    filteredRideData.filter((ride) => resolveStatus(ride) === 'Overdue').length
                    } Overdue`}
                  sx={{
                    backgroundColor: '#d32f2f',
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label={`${summaryCounts.onTime ??
                    filteredRideData.filter((ride) => resolveStatus(ride) === 'On Time').length
                    } On Time`}
                  sx={{
                    backgroundColor: '#2e7d32',
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
              </Box>
            </Paper>
          </Box>
        )}

        <Paper className="mb-3 p-3 border-0 rounded-0 shadow-none">
          <Row className="g-3 align-items-end">
            <Col xs={12} md={3}>
              <TextField
                label="Booking ID"
                placeholder="Enter booking ID"
                size="small"
                fullWidth
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#dee2e6',
                    },
                  },
                }}
              />
            </Col>

            <Col xs={12} md={3}>
              <DatePicker
                label="Due Date"
                value={dueDate}
                onChange={(newValue) => setDueDate(newValue)}
                slotProps={{
                  field: { clearable: true, onClear: () => setDueDate(null) },
                  textField: {
                    size: "small",
                    fullWidth: true,
                  },
                }}
              />
            </Col>
          </Row>
        </Paper>

        <Paper className="border-0 rounded-0 shadow-none">
          {loading ? (
            <CustomLoading message="Fetching Ride Table Data..." />
          ) : (
            <SortableTable
              data={filteredRideData}
              columns={columns}
              emptyMessage="No rides found"
            />
          )}
        </Paper>

        <Pagination
          page={pagination.page}
          rowsPerPage={pagination.rowsPerPage}
          totalItems={totalCount}
          totalPages={pagination.totalPages}
          startItem={pagination.startItem}
          endItem={pagination.endItem}
          rowsPerPageOptions={[5, 10, 25, 50]}
          showRowsPerPage
          onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          onGoToNextPage={pagination.goToNextPage}
          onGoToPreviousPage={pagination.goToPreviousPage}
        />
      </Container>
    </LocalizationProvider>
  );
};

export default RideManagementPage;
